import React, { FC, useState, useMemo, useEffect } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission } from '../../../hooks';
import { useUser } from '../../../hooks/useUser';
import Table from '../../../common/components/Table/Table';
import { ContextMenuAction } from '../../../common/components/Table/types';
import { useTempOverviewData, useTempOverviewDownload, useTempDatasetDownload, useDeleteTempDataset, TempOverviewData } from './hooks';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

import { 
  MainContent, 
  OverviewContainer, 
  TableSection
} from './ENO1TempOverview.styled';

const ENO1TempOverview: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<TempOverviewData | null>(null);
  
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/eno1-temp-overview', '');
  const { data: userData, isLoading: userLoading } = useUser();

  // Fetch temp overview data with pagination
  const { data: tempOverviewData, isLoading: isDataLoading, refetch } = useTempOverviewData({
    page,
    size: pageSize,
  });
  console.log('Temp Overview Data:', tempOverviewData);
  console.log('First row data:', tempOverviewData?.content?.[0]);

  // Download mutation
  const downloadMutation = useTempOverviewDownload({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Settings downloaded successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: `Failed to download settings: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  // Dataset download mutation
  const datasetDownloadMutation = useTempDatasetDownload({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Dataset downloaded successfully',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to download dataset',
        severity: 'error',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useDeleteTempDataset({
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        message: data.message || 'Record deleted successfully',
        severity: 'success',
      });
      setDeleteModalOpen(false);
      setSelectedRow(null);
      refetch(); // Refresh the data
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: `Failed to delete record: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  // Action handlers
  const handleView = (row: TempOverviewData) => {
    console.log('View clicked for:', row);
    // Check if file is temporary or permanent and navigate accordingly
    if (row.isTemp === false) {
      // Permanent file - redirect to classical flow
      navigate(`/eno1/messages/samples/${row.dataSetId}`);
    } else {
      // Temporary file - redirect to temp samples view
      navigate(`/sampling-planning/temp-samples/eno1/${row.dataSetId}`);
    }
  };

  const handleSave = (row: TempOverviewData) => {
    console.log('Save clicked for:', row);
    setSnackbar({
      open: true,
      message: `Saving changes for ${row.designatedOperator} - ${row.year} ${row.quarter}`,
      severity: 'success',
    });
    // Add your save logic here
  };

  const handleDownload = (row: TempOverviewData) => {
    console.log('Download clicked for:', row);
    datasetDownloadMutation.mutate({
      datasetId: row.dataSetId,
      isTemp: row.isTemp ?? true, // Default to true if undefined
    });
  };

  const handleDelete = (row: TempOverviewData) => {
    console.log('Delete clicked for:', row);
    
    // Check if file is permanent (isTemp = false)
    if (row.isTemp === false) {
      setSnackbar({
        open: true,
        message: 'This is a perm file! You need to delete it from the classical flow',
        severity: 'error',
      });
      return;
    }
    
    // Only allow deletion of temporary files
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedRow) {
      // Use dataSetId from the API response
      const datasetID = selectedRow.dataSetId;
      console.log('Deleting with datasetID:', datasetID, 'from row:', selectedRow);
      deleteMutation.mutate({
        datasetID: datasetID,
      });
    }
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setSelectedRow(null);
  };


  // Define table columns
  const columns = useMemo(() => [
    {
      Header: 'ID',
      accessor: 'dataSetId',
      width: '60px',
    },
    {
      Header: 'DO',
      accessor: 'designatedOperator',
      width: '80px',
    },
    {
      Header: 'Year',
      accessor: 'year',
      width: '80px',
    },
    {
      Header: 'Quarter',
      accessor: 'quarter',
      width: '85px',
    },
    {
      Header: 'File Name',
      accessor: 'fileName',
      width: '200px',
      Cell: ({ value }: { value: string | null }) => value || 'No file uploaded',
    },
  ], []);

  // Handle context menu actions
  const handleContextMenuAction = (action: string, row: TempOverviewData) => {
    switch (action) {
      case 'ViewSamples':
        handleView(row);
        break;
      case 'edit':
        handleSave(row);
        break;
      case 'download':
        handleDownload(row);
        break;
      case 'delete':
        handleDelete(row);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Custom actions for the table
  const customActions = useMemo(() => [
    {
      action: 'ViewSamples' as ContextMenuAction,
      label: 'View Samples',
      icon: <VisibilityIcon sx={{ fontSize: 16 }} />,
    },
    {
      action: 'download' as ContextMenuAction,
      label: 'Download',
      icon: <SaveIcon sx={{ fontSize: 16 }} />,
    },
    {
      action: 'delete' as ContextMenuAction,
      label: 'Delete',
      icon: <DeleteIcon sx={{ fontSize: 16 }} />,
    },
  ], []);

  // Custom action buttons for the actions column
  const customActionButtons = useMemo(() => [
    {
      icon: <VisibilityIcon sx={{ fontSize: 18 }} />,
      tooltip: 'View Samples',
      onClick: (row: TempOverviewData) => handleView(row),
      testId: 'view-button',
    },
    {
      icon: <SaveIcon sx={{ fontSize: 18 }} />,
      tooltip: 'Download',
      onClick: (row: TempOverviewData) => handleDownload(row),
      testId: 'download-button',
    },
    {
      icon: <DeleteIcon sx={{ fontSize: 18 }} />,
      tooltip: 'Delete',
      onClick: (row: TempOverviewData) => handleDelete(row),
      testId: 'delete-button',
    },
  ], []);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/sampling-planning/temp-overview');
  };

  const handleDownloadSettings = () => {
    downloadMutation.mutate();
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
  };

  const showLoading = permissionLoading || userLoading || isDataLoading;
  const hasData = tempOverviewData?.content && tempOverviewData.content.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='temp-overview-container'>
      <Grid container data-testid="temp-overview-grid-container">
        {/* First Row - SectionBar */}
        <Grid item xs={12} data-testid="temp-overview-section-bar-container">
          <SectionBar
            title='eNO1 Temp Overview'
            subtitle='Sampling Planning'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            data-testid="temp-overview-section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }} data-testid="temp-overview-main-row">
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} data-testid="temp-overview-sidebar-container">
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="temp-overview-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='temp-overview-main-content'>
            <OverviewContainer data-testid="temp-overview-overview-container">
              {showLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="temp-overview-loading-indicator">
                  <CircularProgress data-testid="temp-overview-loading-spinner" />
                </Box>
              ) : !hasPermission ? (
                <Alert severity='warning' data-testid="temp-overview-permission-error-alert">You don't have permission to view this section</Alert>
              ) : (
                <TableSection data-testid="temp-overview-table-section">
                  {/* Table Section */}
                  {hasData ? (
                    <>
                      <Table
                        data={tempOverviewData?.content || []}
                        columns={columns}
                        page={page}
                        pageSize={pageSize}
                        totalCount={tempOverviewData?.totalElements}
                        disableAutoSizing={false}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        maxWidth='1400px'
                        isLoading={isDataLoading}
                        customActions={customActions}
                        customActionButtons={customActionButtons}
                        hideDefaultActions={true}
                        rowEventHandlers={{
                          onContextMenuAction: handleContextMenuAction,
                        }}
                        data-testid="temp-overview-data-table"
                      />
                    </>
                  ) : null}

                  {/* No Data Message */}
                  {!hasData && !isDataLoading && (
                    <Box sx={{ textAlign: 'center', py: 4 }} data-testid="temp-overview-no-data-message">
                      <Typography variant="body1" color="text.secondary" data-testid="temp-overview-no-data-text">
                        No temp overview data available. Please upload a file to get started.
                      </Typography>
                    </Box>
                  )}
                </TableSection>
              )}
            </OverviewContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid="temp-overview-notification-snackbar"
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        row={selectedRow}
        isDeleting={deleteMutation.isPending}
        data-testid="temp-overview-delete-modal"
      />
    </Box>
  );
};

export default ENO1TempOverview; 